import { useMutation } from "@tanstack/react-query";
import { Text } from "@ui-kitten/components";
import BaseCard, { BaseCardListItem } from "components/BaseCard";
import EventBanner from "components/EventBanner";
import MainContainer from "components/MainContainer";
import ShareDialog from "components/ShareDialog";
import Spacer from "components/Spacer";
import { setTickets as setChangeSeatsTickets } from "features/slices/changeSeatsSlice";
import { AccountTicketDetails, TicketDetails } from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import useMarketingAssetsSections from "hooks/useMarketingAssetsSections";
import { EventDetailScreenProps } from "navigation/types";
import i18n from "plugins/localization";
import { releaseTicketFetcher } from "queries/ticket.queries";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { View } from "react-native";
import { RefreshControl } from "react-native-web-refresh-control";
import { formatEventDate, isSportsEvent } from "utils/eventUtils";
import toast from "utils/toast";
import EventActions from "./EventActions";
import EventDetailFooter from "./EventDetailFooter";
import GuestPasses from "./GuestPasses";
import { isTicketReservedByGuestComponent } from "./helpers";
import RefundDialog from "./RefundDialog";
import ReleaseTicketDialog from "./ReleaseTicketDialog";
import SeatInfo from "./SeatInfo";

const EventDetail: React.FC<EventDetailScreenProps> = ({
  navigation,
  route,
}) => {
  const dispatch = useAppDispatch();
  const { groupCode } = route.params;
  const { accountEvents: events, account, fetchAccount } = useAccount();
  const [eventDetail, setEventDetail] = useState<AccountTicketDetails | null>(
    null,
  );
  const [tickets, setTickets] = useState<TicketDetails[]>([]);
  const [releaseTicketDialog, setReleaseTicketDialog] = useState(false);
  const [refundDialog, setRefundDialog] = useState(false);

  const [transferDialog, setTransferDialog] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const { isSectionMarketingAssets } = useMarketingAssetsSections(
    eventDetail?.tickets[0].planPackageId,
  );

  const { mutateAsync: releaseTicketRequest, isLoading: loading } =
    useMutation(releaseTicketFetcher);

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchAccount();
    setRefreshing(false);
  };

  const [releaseTickets, setReleaseTickets] = useState<TicketDetails[]>([]);

  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedTicketIds, setSelectedTicketIds] = useState<Set<number>>(
    new Set(),
  );

  const toggleTicket = (ticket: TicketDetails) => {
    setSelectedTicketIds((prev) => {
      const next = new Set(prev);
      next.has(ticket.id) ? next.delete(ticket.id) : next.add(ticket.id);
      return next;
    });
  };

  const enterSelectionMode = () => {
    setSelectedTicketIds(new Set());
    setSelectionMode(true);
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedTicketIds(new Set());
  };

  const handleReleaseSelected = () => {
    const selected = tickets.filter((t) => selectedTicketIds.has(t.id));
    exitSelectionMode();
    handleReleaseTicketBtnPress(selected);
  };

  const openDialog = (tickets: TicketDetails[]) => {
    // https://github.com/FanRally/fanrally-core/issues/1209
    // show refund dialog only when there is charge associated with some ticket
    // and the ticket belongs to the same account
    // so do not show the refund dialog if ticket is a guest pass already claimed by someone
    if (
      tickets.some(
        (ticket) =>
          ticket.charge !== null &&
          ticket.accountId === ticket.reservedByAccountId,
      )
    ) {
      setRefundDialog(true);
    } else {
      setReleaseTicketDialog(true);
    }
  };

  const handleReleaseTicketBtnPress = (tickets: TicketDetails[]) => {
    setReleaseTickets(tickets);
    openDialog(tickets);
  };

  /**
   *  https://github.com/FanRally/fanrally-core/issues/1226
   *  You will need to supply all the ticket ids for each ticket in the group code that has TicketDetails.reservedByAccountId = current account id.
   *  If all ids are not specified, the request will fail.
   *  This should include all tickets reserved by the account for this group code, regardless of the current account on the ticket.
   */
  const findTicketsReservedByCurrentAccount = useCallback(() => {
    if (!eventDetail || !account) return [];
    return eventDetail.groupCode.tickets.filter(
      (groupTicket) => groupTicket.reservedByAccountId === account.id,
    ) as unknown as TicketDetails[];
  }, [eventDetail, account]);

  // Find all tickets that are releasable and open the confirm dialog
  const handleReleaseAllBtnPress = () => {
    const ticketProvider = tickets[0].provider;
    let releasableTickets: TicketDetails[] = [];

    if (ticketProvider === "AXS") {
      releasableTickets = findTicketsReservedByCurrentAccount();
    } else {
      releasableTickets = tickets.filter(
        (ticket) =>
          ticket.status === "RESERVED" ||
          (ticket.status === "ISSUED" && ticket.allowIssuedTransfer),
      );
    }
    if (releasableTickets.length === 0) {
      toast.warning(i18n.t("screens.EventDetail.cannotReleaseWarning"));
      return;
    }
    setReleaseTickets(releasableTickets);
    openDialog(releasableTickets);
  };

  const handleReleaseTickets = async () => {
    try {
      const ticketIds = releaseTickets.map((ticket) => ticket.id);
      await releaseTicketRequest(ticketIds);
      toast.success(i18n.t("screens.EventDetail.reservationReleasedMessgae"));
      fetchAccount();
    } finally {
      setReleaseTicketDialog(false);
    }
  };

  // Show guest passes tile if account has guest tickets
  const hasGuestTicketsInAccount = eventDetail?.tickets.some(
    (ticket) =>
      ticket.reservedByAccountId === account?.id &&
      isTicketReservedByGuestComponent(ticket) &&
      (ticket.status === "RESERVED" ||
        (ticket.status === "ISSUED" && ticket.allowIssuedTransfer)),
  );

  const hasTransferableNonGuestTickets = useMemo(() => {
    if (!account) return false;
    return tickets.some(
      (ticket) =>
        ticket.reservedByAccountId === account.id &&
        ticket.transferability !== "NONE" &&
        !isTicketReservedByGuestComponent(ticket) &&
        !isSectionMarketingAssets(ticket.seat.section) &&
        ticket.provider !== "TFL" &&
        (ticket.status === "RESERVED" ||
          (ticket.status === "ISSUED" && ticket.allowIssuedTransfer)),
    );
  }, [account, tickets]);

  useEffect(() => {
    if (!account || !events) return;
    const eventDetail = events.find(
      (eventDetail) => eventDetail.groupCode.groupCode === groupCode,
    );
    if (eventDetail === undefined) {
      setTimeout(() => {
        navigation.replace("Events");
      }, 100);
      return;
    }
    setTickets(eventDetail.tickets);
    dispatch(setChangeSeatsTickets(eventDetail.tickets));
    setEventDetail(eventDetail);
  }, [events, account]);

  if (!eventDetail || tickets.length === 0) return null;

  const isPremiumEvent = eventDetail.premiumCode !== null;
  const premiumClassType = eventDetail.premiumClass?.type;
  const [ticket] = tickets;

  // Do not show barcode texts for marketing assets/experience events
  const shouldShowBarcodeTexts = !isSectionMarketingAssets(ticket.seat.section);

  const findPlanPackageById = (planPackageId: number) => {
    if (account?.subscriptions.length === 0) return null;

    const planPackage = account!.subscriptions.find(
      (sub) => sub.planPackage.id === planPackageId,
    )?.planPackage;

    if (!planPackage) return null;
    return planPackage;
  };

  const shouldShowEventActions = ticket.provider !== "TFL";

  if (tickets.length === 0) return null;

  return (
    <MainContainer
      goBackAction={() => navigation.navigate("Events")}
      title={i18n.t("screens.EventDetail.title")}
      grayBackground
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
      }
    >
      <View style={{ padding: 15 }}>
        <EventBanner event={eventDetail.event} />

        {shouldShowBarcodeTexts && (
          <View style={{ marginTop: 15 }}>
            <EventDetailFooter tickets={tickets} groupCode={groupCode} />
          </View>
        )}

        <Spacer />

        {!isSportsEvent(eventDetail.event.type) && (
          <>
            <BaseCard title="Event Info">
              <BaseCardListItem
                showArrow={false}
                title={eventDetail.event.name}
              />
              <BaseCardListItem
                showArrow={false}
                title={formatEventDate(eventDetail.event)}
              />
              <BaseCardListItem
                showArrow={false}
                title={`${eventDetail.event.venueName} - ${eventDetail.event.venueCity}, ${eventDetail.event.venueState}`}
              />
            </BaseCard>

            <Spacer />
          </>
        )}

        {isPremiumEvent && (
          <>
            <BaseCard title="Location">
              <BaseCardListItem
                showArrow={false}
                title={`${ticket.seat.premiumClass!.name}`}
                description={
                  <View>
                    {!["EXPERIENCE", "RSVP"].includes(
                      eventDetail.event.type,
                    ) && (
                      <Text category="c1">{ticket.seat.sectionDisplay}</Text>
                    )}
                    {/* only show X seats if preimum event is not a marketing assets event */}
                    {!isSectionMarketingAssets(ticket.seat.section) && (
                      <Text category="c1">
                        {tickets.length}{" "}
                        {
                          i18n.t("common.seat", {
                            count: tickets.length,
                          }) as string
                        }
                      </Text>
                    )}
                  </View>
                }
              ></BaseCardListItem>
            </BaseCard>
            <Spacer />
          </>
        )}
        {(!isPremiumEvent || premiumClassType === "SEATS") && (
          <SeatInfo
            groupCode={groupCode}
            reservedByAccountId={eventDetail.groupCode.accountId}
            groupTickets={eventDetail.groupCode.tickets}
            tickets={tickets}
            releaseTicket={handleReleaseTicketBtnPress}
            planPackage={findPlanPackageById(tickets[0].planPackageId)}
            selectionMode={selectionMode}
            selectedTicketIds={selectedTicketIds}
            onToggleTicket={toggleTicket}
            onEnterSelectionMode={enterSelectionMode}
            onExitSelectionMode={exitSelectionMode}
            onReleaseSelected={handleReleaseSelected}
          />
        )}

        {hasTransferableNonGuestTickets && (
          <>
            <Spacer style={{ marginVertical: 10 }}></Spacer>
            <BaseCard title={i18n.t("screens.EventDetail.manageSeats")}>
              <BaseCardListItem
                title={i18n.t("screens.EventDetail.transferSeatsTitle")}
                onPress={() => setTransferDialog(true)}
              ></BaseCardListItem>
            </BaseCard>
          </>
        )}

        {hasGuestTicketsInAccount && (
          <>
            <Spacer style={{ marginVertical: 10 }}></Spacer>
            <GuestPasses groupCode={groupCode} />
          </>
        )}

        <Spacer style={{ marginVertical: 10 }}></Spacer>

        {shouldShowEventActions && (
          <EventActions
            seat={ticket.seat}
            event={eventDetail.event}
            groupCode={groupCode}
            releaseAllBtnPress={handleReleaseAllBtnPress}
            groupTickets={eventDetail.groupCode.tickets}
            tickets={eventDetail.tickets}
          />
        )}

        <Spacer style={{ marginVertical: 20 }}></Spacer>

        {shouldShowBarcodeTexts && (
          <EventDetailFooter tickets={tickets} groupCode={groupCode} />
        )}

        {refundDialog && (
          <RefundDialog
            visible={refundDialog}
            setVisible={setRefundDialog}
            tickets={releaseTickets}
            cancelText={i18n.t("common.cancel")}
            onCancel={() => setRefundDialog(false)}
            okText={i18n.t("common.confirm")}
            onOk={() => {
              setRefundDialog(false);
              // https://github.com/FanRally/fanrally-core/issues/900#issuecomment-1268725920
              setTimeout(() => {
                setReleaseTicketDialog(true);
              });
            }}
            planPackage={findPlanPackageById(tickets[0].planPackageId)}
          />
        )}
        {releaseTicketDialog && (
          <ReleaseTicketDialog
            visible={releaseTicketDialog}
            setVisible={setReleaseTicketDialog}
            tickets={releaseTickets}
            cancelText={i18n.t("common.cancel")}
            onCancel={() => setReleaseTicketDialog(false)}
            okText={i18n.t("screens.EventDetail.releaseButton")}
            okBtnProps={{ status: "danger", testID: "release-button" }}
            onOk={handleReleaseTickets}
            loading={loading}
          />
        )}
        {transferDialog && (
          <ShareDialog
            visible={transferDialog}
            setVisible={setTransferDialog}
            path="claim_seats"
            title={i18n.t("screens.EventDetail.transferSeatsTitle")}
            accountEvent={eventDetail}
            type="seats"
            ticketProvider={eventDetail.event.ticketProvider}
          ></ShareDialog>
        )}
      </View>
    </MainContainer>
  );
};

export default EventDetail;
